import { PageLayout } from '@/components/layout'
import { Footer } from '@/components/layout/Footer'
import { useAuth } from '@/hooks/useAuth'
import {
  DashboardServiceCard,
  ActivityFeed,
  UsageSummary,
} from '@/components/dashboard'
import { useDashboardOverview } from '@/hooks/useDashboard'
import { LoadingSpinner } from '@/components/common'

export const Dashboard = () => {
  const { user } = useAuth()
  const { data: dashboardData, isLoading, error } = useDashboardOverview(user?.organizationId)

  // Transform API data to service cards format
  const mapSubscriptionStatus = (status?: string): 'none' | 'pending' | 'active' | 'trial' | 'expired' | undefined => {
    if (!status) return 'none'
    if (status === 'cancelled') return 'expired'
    if (['pending', 'active', 'trial', 'expired'].includes(status)) return status as 'pending' | 'active' | 'trial' | 'expired'
    return 'none'
  }

  // Service definitions
  const allServices = [
    {
      id: 'blis' as const,
      name: 'BLIS',
      title: 'Enterprise Analytics',
      description:
        'Deep dive into geospatial data trends. Visualize movement patterns and optimize logistics with historical data.',
      icon: 'analytics',
      iconColor: 'bg-blue-50 p-3 text-primary dark:bg-blue-900/20 dark:text-blue-300',
    },
    {
      id: 'izg' as const,
      name: 'IZG',
      title: 'Real-time Zones',
      description:
        'Intelligent Zone Geofencing for immediate boundary control. Get alerts the second an asset moves.',
      icon: 'radar',
      iconColor: 'bg-emerald-50 p-3 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400',
    },
  ]

  // Filter to only show services that the user has subscribed to
  // Exclude cancelled subscriptions - they should not appear
  // A service is considered "subscribed" if there's any subscription record (except cancelled)
  // If no subscriptions exist, show empty state
  const subscriptions = (dashboardData?.subscriptions || []).filter(
    (sub) => sub.status?.toLowerCase() !== 'cancelled'
  )
  const subscribedServices = allServices
    .filter((service) => {
      // Check if there's a subscription for this service (excluding cancelled)
      const subscription = subscriptions.find(
        (s) => s.service_name === service.name
      )
      return subscription !== undefined // Show if subscription exists (any status except cancelled)
    })
    .map((service) => {
      const subscription = subscriptions.find(
        (s) => s.service_name === service.name
      )
      return {
        ...service,
        subscriptionStatus: mapSubscriptionStatus(subscription?.status),
        subscriptionPlan: subscription?.plan_tier || 'None',
      }
    })

  const services = subscribedServices

  if (isLoading) {
    return (
      <PageLayout showHeader={true}>
        <div className="p-6 md:p-8 lg:p-10 flex items-center justify-center min-h-[400px]">
          <LoadingSpinner size="lg" />
        </div>
      </PageLayout>
    )
  }

  if (error) {
    return (
      <PageLayout showHeader={true}>
        <div className="p-6 md:p-8 lg:p-10">
          <div className="max-w-7xl mx-auto">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-red-600 dark:text-red-400">
                Error loading dashboard: {error instanceof Error ? error.message : 'Unknown error'}
              </p>
            </div>
          </div>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout showHeader={true}>
      <div className="p-6 md:p-8 lg:p-10">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-text-main-light dark:text-text-main-dark mb-2">
              Welcome back{user?.name ? `, ${user.name}` : ''}!
            </h1>
            <p className="text-text-sub-light dark:text-text-sub-dark">
              Manage your location intelligence services and subscriptions.
            </p>
          </div>

          {/* Subscriptions Section */}
          <section id="subscriptions" className="mb-12 scroll-mt-24">
            <h2 className="text-2xl font-semibold mb-6 text-text-main-light dark:text-text-main-dark">
              Subscriptions
            </h2>
            {subscriptions.length > 0 ? (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
                <div className="space-y-4">
                  {subscriptions.map((sub) => {
                    const status = sub.status?.toLowerCase() || 'unknown'
                    const periodEnd = sub.current_period_end
                      ? new Date(sub.current_period_end).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })
                      : null

                    // Status badge configuration
                    const getStatusBadge = () => {
                      switch (status) {
                        case 'pending':
                          return {
                            label: 'Pending Approval',
                            icon: 'schedule',
                            className:
                              'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
                          }
                        case 'active':
                          return {
                            label: 'Active',
                            icon: 'check_circle',
                            className:
                              'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
                          }
                        case 'trial':
                          return {
                            label: 'Trial',
                            icon: 'timer',
                            className:
                              'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
                          }
                        case 'expired':
                          return {
                            label: 'Expired',
                            icon: 'cancel',
                            className:
                              'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
                          }
                        case 'cancelled':
                          return {
                            label: 'Cancelled',
                            icon: 'block',
                            className:
                              'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
                          }
                        default:
                          return {
                            label: 'Unknown',
                            icon: 'help',
                            className:
                              'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
                          }
                      }
                    }

                    const statusBadge = getStatusBadge()

                    return (
                      <div
                        key={sub.service_name}
                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 bg-white dark:bg-gray-800 rounded-lg border border-slate-200 dark:border-gray-700"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-lg font-semibold text-text-main-light dark:text-text-main-dark">
                              {sub.service_name}
                            </span>
                            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                              {sub.plan_tier}
                            </span>
                          </div>
                          {periodEnd && (
                            <p className="text-sm text-text-sub-light dark:text-text-sub-dark">
                              <span className="material-symbols-outlined text-sm align-middle mr-1">
                                calendar_today
                              </span>
                              {status === 'active' || status === 'trial'
                                ? `Renews on ${periodEnd}`
                                : `Ended on ${periodEnd}`}
                              {sub.cancel_at_period_end && status === 'active' && (
                                <span className="ml-2 text-orange-600 dark:text-orange-400">
                                  (Cancelling at period end)
                                </span>
                              )}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <span
                            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${statusBadge.className}`}
                          >
                            <span className="material-symbols-outlined text-base">
                              {statusBadge.icon}
                            </span>
                            {statusBadge.label}
                          </span>
                        </div>
                      </div>
                    )
                  })}
                </div>
                {subscriptions.some((sub) => sub.status?.toLowerCase() === 'pending') && (
                  <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <p className="text-sm text-yellow-800 dark:text-yellow-300">
                      <span className="material-symbols-outlined text-sm align-middle mr-1">info</span>
                      Your subscription requests are pending admin approval. You can browse the website, but service access will be granted once approved.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg p-6 text-center">
                <p className="text-text-sub-light dark:text-text-sub-dark">No subscriptions found.</p>
              </div>
            )}
          </section>

          {/* Services Section */}
          <section id="services" className="mb-12 scroll-mt-24">
            <h2 className="text-2xl font-semibold mb-6 text-text-main-light dark:text-text-main-dark">
              Your Services
            </h2>
            {services.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {services.map((service) => (
                  <DashboardServiceCard key={service.id} service={service} />
                ))}
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg p-8 text-center">
                <span className="material-symbols-outlined text-4xl text-text-sub-light dark:text-text-sub-dark mb-4 block">
                  subscription
                </span>
                <p className="text-text-sub-light dark:text-text-sub-dark mb-2">
                  No services subscribed yet.
                </p>
                <p className="text-sm text-text-sub-light dark:text-text-sub-dark">
                  Contact us to subscribe to BLIS or IZG services.
                </p>
              </div>
            )}
          </section>

          {/* Usage Section */}
          <section id="usage" className="mb-12 scroll-mt-24">
            <h2 className="text-2xl font-semibold mb-6 text-text-main-light dark:text-text-main-dark">
              Usage Summary
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <ActivityFeed />
              </div>
              <div>
                <UsageSummary usageData={dashboardData?.usage_summary} />
              </div>
            </div>
          </section>
        </div>
      </div>
      <Footer />
    </PageLayout>
  )
}

